/**
 * GameStore — Zustand store bridging the pure engine to React.
 *
 * Every mutation clones the state (`structuredClone`) before calling engine
 * actions, so React sees fresh references and undo-ability stays trivial.
 * The tick loop lives in `useGameLoop`, not here.
 */

import { create } from 'zustand';
import type { GameState, MarketingAllocation, Difficulty, AwaySummary } from '@/game/types';
import {
  actionAcceptExit,
  actionDeclineExit,
  actionDesignProduct,
  actionDrawCredit,
  actionExpandWarehouse,
  actionHire,
  actionOpenShowroom,
  actionPlacePO,
  actionRepayCredit,
  actionResolveEvent,
  actionSetMarketing,
  actionSetPrice,
  actionStartResearch,
  actionUpgradeShowroom,
  createNewGame,
  simulateAway,
  tickDay,
} from '@/game/engine';
import { clearSave, loadSave, persistSave } from '@/game/save';
import { sfx } from '@/game/audio';

export type Speed = 0 | 1 | 2 | 3; // paused, normal, fast, ultra
export const SPEED_MS: Record<Speed, number> = { 0: Infinity, 1: 1000, 2: 350, 3: 120 };

interface GameStore {
  state: GameState | null;
  speed: Speed;
  awaySummary: AwaySummary | null;
  lastOutcomeToast: string | null;
  soundOn: boolean;

  newGame: (companyName: string, difficulty: Difficulty) => void;
  continueGame: () => boolean;
  abandonGame: () => void;
  setSpeed: (s: Speed) => void;
  togglePause: () => void;
  setSound: (on: boolean) => void;

  /** Advance exactly one day (manual single-step). */
  stepDay: () => void;
  /** Internal: called by the loop. */
  _tick: () => void;

  placePO: (productId: string, supplierId: string, units: number) => string | null;
  setPrice: (productId: string, price: number) => void;
  designProduct: (spec: { name: string; archetypeId: GameState['products'][number]['archetype']; quality: number; price: number }) => string | null;
  startResearch: (nodeId: string) => string | null;
  hire: (hireId: string) => string | null;
  setMarketing: (budget: number, allocation: MarketingAllocation, enabled: boolean) => void;
  drawCredit: (amount: number) => string | null;
  repayCredit: (amount: number) => string | null;
  expandWarehouse: () => string | null;
  openShowroom: () => string | null;
  upgradeShowroom: (id: string) => string | null;
  resolveEvent: (choiceId: string | null) => void;
  acceptExit: () => void;
  declineExit: () => void;
  dismissAwaySummary: () => void;
}

function clone(s: GameState): GameState {
  return structuredClone(s);
}

export const useGameStore = create<GameStore>()((set, get) => ({
  state: null,
  speed: 1,
  awaySummary: null,
  lastOutcomeToast: null,
  soundOn: sfx.enabled,

  newGame: (companyName, difficulty) => {
    const state = createNewGame({ companyName, difficulty });
    persistSave(state);
    set({ state, speed: 1, awaySummary: null });
  },

  continueGame: () => {
    const res = loadSave();
    if (!res.ok) return false;
    // Offline catch-up.
    const elapsed = Date.now() - (res.state.meta.lastPlayed ?? Date.now());
    const working = clone(res.state);
    const days = simulateAway(working, elapsed);
    let summary: AwaySummary | null = null;
    if (days > 0 && !working.gameOver) {
      summary = {
        daysSimulated: days,
        cashDelta: working.player.cash - res.state.player.cash,
        unitsSold: working.stats.totalUnits - res.state.stats.totalUnits,
        highlights: working.news.slice(0, 4).map((n) => n.headline),
      };
    }
    persistSave(working);
    set({ state: working, awaySummary: summary, speed: 1 });
    return true;
  },

  abandonGame: () => {
    clearSave();
    set({ state: null, awaySummary: null });
  },

  setSpeed: (speed) => set({ speed }),
  togglePause: () => set((st) => ({ speed: st.speed === 0 ? 1 : 0 })),
  setSound: (on) => {
    sfx.setEnabled(on);
    set({ soundOn: on });
  },

  stepDay: () => {
    const cur = get().state;
    if (!cur || cur.gameOver || cur.pendingEventId) return;
    const next = clone(cur);
    tickDay(next);
    persistSave(next);
    set({ state: next });
  },

  _tick: () => {
    const { state, speed } = get();
    if (!state || speed === 0 || state.pendingEventId || state.exitOffer) return;
    if (document.hidden) return; // iOS throttles anyway; stay deterministic
    const next = clone(state);
    tickDay(next);
    // Weekly autosave cadence + always save on quarter boundaries.
    if (next.time.day % 7 === 0) persistSave(next);
    set({ state: next });
  },

  placePO: (productId, supplierId, units) => {
    const cur = get().state;
    if (!cur) return 'No game.';
    const next = clone(cur);
    const err = actionPlacePO(next, productId, supplierId, units);
    if (!err) { persistSave(next); sfx.play('po'); }
    set({ state: next });
    return err;
  },

  setPrice: (productId, price) => {
    const cur = get().state;
    if (!cur) return;
    const next = clone(cur);
    actionSetPrice(next, productId, price);
    set({ state: next });
  },

  designProduct: (spec) => {
    const cur = get().state;
    if (!cur) return 'No game.';
    const next = clone(cur);
    const err = actionDesignProduct(next, spec);
    if (!err) { persistSave(next); sfx.play('research'); }
    set({ state: next });
    return err;
  },

  startResearch: (nodeId) => {
    const cur = get().state;
    if (!cur) return 'No game.';
    const next = clone(cur);
    const err = actionStartResearch(next, nodeId);
    if (!err) { persistSave(next); sfx.play('research'); }
    set({ state: next });
    return err;
  },

  hire: (hireId) => {
    const cur = get().state;
    if (!cur) return 'No game.';
    const next = clone(cur);
    const err = actionHire(next, hireId);
    if (!err) { persistSave(next); sfx.play('good'); }
    set({ state: next });
    return err;
  },

  setMarketing: (budget, allocation, enabled) => {
    const cur = get().state;
    if (!cur) return;
    const next = clone(cur);
    actionSetMarketing(next, budget, allocation, enabled);
    persistSave(next);
    set({ state: next });
  },

  drawCredit: (amount) => {
    const cur = get().state;
    if (!cur) return 'No game.';
    const next = clone(cur);
    const err = actionDrawCredit(next, amount);
    if (!err) { persistSave(next); sfx.play('coin'); }
    set({ state: next });
    return err;
  },

  repayCredit: (amount) => {
    const cur = get().state;
    if (!cur) return 'No game.';
    const next = clone(cur);
    const err = actionRepayCredit(next, amount);
    if (!err) persistSave(next);
    set({ state: next });
    return err;
  },

  expandWarehouse: () => {
    const cur = get().state;
    if (!cur) return 'No game.';
    const next = clone(cur);
    const err = actionExpandWarehouse(next);
    if (!err) { persistSave(next); sfx.play('po'); }
    set({ state: next });
    return err;
  },

  openShowroom: () => {
    const cur = get().state;
    if (!cur) return 'No game.';
    const next = clone(cur);
    const err = actionOpenShowroom(next);
    if (!err) { persistSave(next); sfx.play('good'); }
    set({ state: next });
    return err;
  },

  upgradeShowroom: (id) => {
    const cur = get().state;
    if (!cur) return 'No game.';
    const next = clone(cur);
    const err = actionUpgradeShowroom(next, id);
    if (!err) { persistSave(next); sfx.play('good'); }
    set({ state: next });
    return err;
  },

  resolveEvent: (choiceId) => {
    const cur = get().state;
    if (!cur || !cur.pendingEventId) return;
    const next = clone(cur);
    actionResolveEvent(next, choiceId);
    persistSave(next);
    set({ state: next });
  },

  acceptExit: () => {
    const cur = get().state;
    if (!cur) return;
    const next = clone(cur);
    actionAcceptExit(next);
    persistSave(next);
    set({ state: next });
  },

  declineExit: () => {
    const cur = get().state;
    if (!cur) return;
    const next = clone(cur);
    actionDeclineExit(next);
    persistSave(next);
    set({ state: next });
  },

  dismissAwaySummary: () => set({ awaySummary: null }),
}));